<i18n>
{
  "fr": {
    "configuratorLayerEditorModalAiGenImage": {
      "placeholder": "Saisissez un texte pour commencer à générer une image.\nChaque génération a un coût énergétique et un impact environnemental. Merci d'utiliser cet outil de manière responsable.",
      "submit": "Envoyer",
      "inputPlaceholder": "Un astronaute à cheval, une banane qui fait du skate, ...",
      "loading": "Génération en cours..."
    }
  }
}
</i18n>

<template>
  <div :class="BLOCK_SELECTOR">
    <ul :class="`${BLOCK_SELECTOR}__messages-list`">
      <li
        v-for="message in messages"
        :key="message.id"
        :class="`${BLOCK_SELECTOR}__message-item ${
          message.fromUser
            ? `${BLOCK_SELECTOR}__message-item--from-user`
            : `${BLOCK_SELECTOR}__message-item--from-bot`
        }`"
      >
        <span v-if="message.fromUser">{{ message.content }}</span>

        <span v-else-if="message.id === -1">
          <span>{{ message.content }}</span>
          <img
            v-if="initialImageUrl"
            :src="initialImageUrl"
            alt="Initial file"
          />
        </span>

        <div v-else>
          <img
            :class="`${BLOCK_SELECTOR}__message-image-response`"
            :src="message.content"
            alt="generated content"
          />
          <BaseBtn
            v-if="generatedUrls.length > 0"
            :class="`${BLOCK_SELECTOR}__select-button`"
            type="button"
            link
            @click="$emit('select-image', generatedFiles[message.index])"
          />
        </div>
      </li>
      <li
        v-if="isLoading"
        :class="`${BLOCK_SELECTOR}__message-item ${BLOCK_SELECTOR}__message-item--from-bot`"
      >
        <LgfSpinner :class="`${BLOCK_SELECTOR}__message-item-spinner`" />
        <span>{{ $t('configuratorLayerEditorModalAiGenImage.loading') }}</span>
      </li>
    </ul>

    <div :class="`${BLOCK_SELECTOR}__action-bar`">
      <textarea
        id="input"
        v-model="prompt"
        :disabled="isLoading"
        :class="`${BLOCK_SELECTOR}__text-input`"
        :placeholder="
          $t('configuratorLayerEditorModalAiGenImage.inputPlaceholder')
        "
        :aria-label="
          $t('configuratorLayerEditorModalAiGenImage.inputPlaceholder')
        "
        type="text"
      ></textarea>
      <BaseBtn
        :disabled="isLoading || !prompt || prompt === ''"
        @click="onSubmit"
      >
        <span>{{ $t('configuratorLayerEditorModalAiGenImage.submit') }}</span>
      </BaseBtn>
    </div>
  </div>
</template>

<script>
import BaseBtn from '../../../BaseBtn';
import LgfSpinner from '../../../LgfSpinner';

export default {
  name: 'ConfiguratorLayerEditorModalAiGenImage',
  emits: ['select-image'],
  components: { BaseBtn, LgfSpinner },
  props: {
    initialImage: {
      type: File,
      default: undefined,
    },
  },
  data() {
    return {
      isLoading: false,
      prompt: '',
      messages: [
        {
          id: -1,
          content: this.$t(
            'configuratorLayerEditorModalAiGenImage.placeholder'
          ),
          fromUser: false,
        },
      ],
      BLOCK_SELECTOR: 'configurator-layer-editor-modal-ai-gen-image',
      generatedFiles: [],
      generatedUrls: [],
    };
  },
  computed: {
    initialImageUrl() {
      return this.initialImage ? URL.createObjectURL(this.initialImage) : null;
    },
  },
  beforeDestroy() {
    if (this.initialImageUrl) {
      URL.revokeObjectURL(this.initialImageUrl);
    }
    if (this.generatedFile) {
      URL.revokeObjectURL(this.generatedFile);
    }
    if (this.imageUrl) {
      URL.revokeObjectURL(this.imageUrl);
    }
  },
  methods: {
    async onSubmit() {
      if (!this.prompt) {
        return;
      }

      this.messages.push({
        id: this.messages.length + 1,
        content: this.prompt,
        fromUser: true,
      });
      this.prompt = '';

      try {
        this.isLoading = true;

        const lastGeneratedFile =
          this.generatedFiles.length > 0
            ? this.generatedFiles[this.generatedFiles.length - 1]
            : this.initialImage;

        const generatedFile = await this.callImagenAIStudio(
          this.messages[this.messages.length - 1].content,
          lastGeneratedFile
        );

        this.generatedFiles.push(generatedFile);

        this.generatedUrls.push(URL.createObjectURL(generatedFile));

        this.messages.push({
          id: this.messages.length + 1,
          content: this.generatedUrls[this.generatedUrls.length - 1],
          index: this.generatedUrls.length - 1,
          fromUser: false,
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async callImagenAIStudio(prompt, sourceFile) {
      const MODEL_ID = 'gemini-3.1-flash-image-preview';
      const API_KEY = process.env.MIX_GEMINI_API_KEY;
      const url = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${API_KEY}`;

      // const completePrompt = `${prompt}. Isolated on a solid #ffffff bright white chroma key background, flat color, no shadows, no gradients.`;
      const completePrompt = `${prompt}`;

      const parts = [{ text: completePrompt }];

      if (sourceFile) {
        const sourceBase64 = await this.fileToBase64(sourceFile);
        parts.push({
          inline_data: {
            mime_type: sourceFile.type || 'image/png',
            data: sourceBase64,
          },
        });
      }

      const payload = {
        contents: [{ parts }],
        generationConfig: {
          temperature: 0.7,
          imageConfig: { aspectRatio: '1:1' },
        },
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Erreur API (${response.status}) : ${errorText}`);
        return null;
      }

      const json = await response.json();
      const responseParts = json.candidates?.[0]?.content?.parts ?? [];
      const imagePart = responseParts.find(p => p.inlineData || p.inline_data);

      if (!imagePart) {
        console.error("L'image n'a pas été trouvée dans la réponse.");
        return null;
      }

      const imageData = imagePart.inlineData || imagePart.inline_data;
      const byteString = atob(imageData.data);
      const bytes = Uint8Array.from(byteString, c => c.codePointAt(0));

      return new File([bytes], `new gen`, {
        type: 'image/png',
      });
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    setLoading(value) {
      if (value) {
        this.isLoading = true;
      } else {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-layer-editor-modal-ai-gen-image {
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  padding: 0.5rem 1rem;

  &__select-button {
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    height: 2.75rem;
    width: 2.75rem;
    z-index: 1;
    background-color: $color-arapawa;
    color: $color-white;
    border-radius: 50%;

    &::before {
      @extend %icon;
      @extend %icon--check;
    }

    &:hover {
      opacity: 0.7;
    }
  }

  &__messages-list {
    list-style: none;
    padding: 0;
    margin: 0;
    flex: 1;
    height: width 100%;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    overflow-y: auto;
  }

  &__message-item {
    padding: 0.5rem;
    border-radius: 0.5rem;
    background-color: #f0f0f0;
    width: fit-content;
    max-width: 85%;
    align-self: flex-end;

    &--from-bot {
      align-self: flex-start;
      position: relative;
      background-color: rgba($color-arapawa, 0.1);
      white-space: pre-line;
    }
  }

  &__message-image-response {
    max-height: 15.625rem;
  }

  &__action-bar {
    flex-shrink: 0;
    display: flex;
    gap: 0.5rem;
  }

  &__text-input {
    height: 3.5rem;
    flex: 1;
    padding: 0.5rem;
    border-radius: 1.25rem;
    border: none;
    line-height: inherit;
    resize: none;
  }
}
</style>
